import { Box, Typography, Tooltip, TextField, useMediaQuery } from '@mui/material';
import { useEffect, useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled, useTheme } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ArrowForward } from '@mui/icons-material';
import {
  useCreateDeveloperKeyMutation,
  useDeleteDeveloperKeyMutation,
  useGetDeveloperKeyQuery,
} from '../../data/api/endPointsSlice';

export interface DeveloperKey {
  api_key: string;
  name: string;
  expiry_date: string;
}

export interface UserDetails {
  displayName: string;
  givenName: string;
  mail: string | null;
  id: string;
}

const GenerateKey = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [developerKey, setDeveloperKey] = useState<DeveloperKey>();
  const [devKeyName, setDevKeyName] = useState('');
  const { data: developerKeyData } = useGetDeveloperKeyQuery();
  const [createDeveloperKey] = useCreateDeveloperKeyMutation();
  const [deleteDeveloperKey] = useDeleteDeveloperKeyMutation();

  useEffect(() => {
    if (developerKeyData) {
      setDeveloperKey({
        api_key: developerKeyData.api_key || '',
        name: developerKeyData.name || '',
        expiry_date: developerKeyData.expiry_date || '',
      });
      setDevKeyName(developerKeyData.name || '');
    }
  }, [developerKeyData]);

  const createUserKey = async () => {
    try {
      if (!devKeyName || devKeyName.trim().length === 0) {
        enqueueSnackbar('Please provide a valid name for your key', {
          variant: 'error',
        });
        return;
      }

      if (devKeyName.length < 4 || devKeyName.length > 50) {
        enqueueSnackbar('Key name must be between 4 and 50 characters', {
          variant: 'error',
        });
        return;
      }

      if (developerKey?.api_key) {
        enqueueSnackbar('You already have a developer key', {
          variant: 'error',
        });
        return;
      }

      const response = await createDeveloperKey(devKeyName).unwrap();
      if (response) {
        setDeveloperKey({
          api_key: response.api_key || '',
          name: response.name || '',
          expiry_date: response.expiry_date,
        });
        enqueueSnackbar('Developer Key successfully generated', {
          variant: 'success',
        }); // not needed now, keeping this in case multiple key invocation comes into play.
      }
    } catch (error) {}
  };

  const revokeUserKey = async () => {
    try {
      if (developerKey && developerKey.api_key) {
        await deleteDeveloperKey(developerKey.api_key).unwrap();
        enqueueSnackbar('Developer key revoked successfully', {
          variant: 'success',
        });
        setDeveloperKey({ api_key: '', name: '', expiry_date: '' });
        setDevKeyName('');
      }
    } catch (error) {
      console.error('Error revoking developer key:', error);
      enqueueSnackbar('Failed to revoke developer key', {
        variant: 'error',
      });
    }
  };

  const handleCopyKey = () => {
    if (developerKey && developerKey.api_key) {
      try {
        navigator.clipboard.writeText(developerKey.api_key);
        // enqueueSnackbar(t("generateKey.keyCopied"), { variant: "success" });
      } catch {
        // enqueueSnackbar(t("generateKey.keyCopiedError"), { variant: "error" });
      }
    }
  };

  const BorderlessTextField = styled(TextField)`
    & .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  `;

  return (
    <>
      <Box>
        <Typography>
          To generate developer key, please provide Token name in the below tab and click on the arrow icon
          <ArrowForward style={{ verticalAlign: 'middle' }} />
        </Typography>

        <Typography
          sx={{
            paddingLeft: '10px',
            fontWeight: '800',
            color: '#6F6C6C',
            mt: '15px',
            mb: '5px',
          }}
        >
          Token Name:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Left Section */}
          <Box>
            {developerKey && developerKey.name ? (
              <BorderlessTextField
                variant="outlined"
                value={developerKey.name}
                type="text"
                sx={{ width: isMobile ? '100%' : '420px' }}
                InputProps={{ readOnly: true }}
              />
            ) : (
              <TextField
                value={devKeyName}
                onChange={(event) => setDevKeyName(event.target.value)}
                placeholder="Name Your Key"
                variant="outlined"
                size="small"
                sx={{
                  width: isMobile ? '100%' : '420px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            )}
          </Box>

          {/* Right Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              width: '30px',
              height: '30px',
              marginRight: '10px',
              cursor: 'pointer',
            }}
          >
            {developerKey && developerKey.name ? (
              <Tooltip title="Revoke" arrow placement="top">
                <DeleteForeverIcon
                  sx={{
                    color: 'grey',
                    height: '30px',
                    '&:hover': {
                      color: '#3C82F6',
                    },
                  }}
                  onClick={revokeUserKey}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Generate" arrow>
                <ArrowForwardOutlinedIcon
                  sx={{
                    color: 'grey',
                    '&:hover': {
                      color: '#3C82F6',
                    },
                  }}
                  onClick={createUserKey}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {developerKey && developerKey.api_key && (
          <>
            <Typography sx={{ paddingLeft: '10px', fontWeight: '800', color: '#6F6C6C' }}>Token key</Typography>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ alignItems: 'center' }}>
                <BorderlessTextField
                  variant="outlined"
                  value={developerKey.api_key}
                  type="password"
                  InputProps={{ readOnly: true }}
                  sx={{ width: '420px' }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  width: '30px',
                  height: '30px',
                  marginRight: '10px',
                  cursor: 'pointer',
                  marginTop: '15px',
                }}
              >
                <Tooltip title="Copy Key" arrow>
                  <ContentCopyIcon
                    onClick={handleCopyKey}
                    sx={{
                      color: 'grey',
                      height: '20px',
                      '&:hover': {
                        color: '#3C82F6',
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
        {developerKey && developerKey.expiry_date && (
          <Box>
            <Typography sx={{ color: '#6F6C6C', paddingLeft: '12px' }}>
              <strong> Valid Till</strong> {developerKey?.expiry_date}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default GenerateKey;
